import { useState, useCallback, useRef, useEffect } from 'react';

function getWsBase() {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (backendUrl) {
    return backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}`;
}

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1500;

export function useTrace() {
  const [traceState, setTraceState] = useState('idle'); // idle, connecting, tracing, complete, error
  const [hops, setHops] = useState([]);
  const [allHops, setAllHops] = useState([]);
  const [visibleHops, setVisibleHops] = useState([]);
  const [traceInfo, setTraceInfo] = useState(null);
  const [traceSummary, setTraceSummary] = useState(null);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);
  const intentionalCloseRef = useRef(false);
  const traceCompletedRef = useRef(false);
  const pendingParamsRef = useRef(null);

  const cleanup = useCallback(() => {
    intentionalCloseRef.current = true;
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (wsRef.current) {
      try { wsRef.current.close(); } catch(e) {}
      wsRef.current = null;
    }
  }, []);

  const connectWebSocket = useCallback((target, sourceIp, isRetry = false) => {
    const wsBase = getWsBase();
    const wsUrl = `${wsBase}/api/ws/trace`;

    if (!isRetry) {
      retryCountRef.current = 0;
      traceCompletedRef.current = false;
      pendingParamsRef.current = { target, sourceIp };
      setTraceState('connecting');
      setHops([]);
      setTraceInfo(null);
      setTraceSummary(null);
      setError(null);
    }

    intentionalCloseRef.current = false;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        retryCountRef.current = 0;
        const params = pendingParamsRef.current || { target, sourceIp };
        ws.send(JSON.stringify({
          target: params.target || 'whatismyroute.com',
          source_ip: params.sourceIp || ''
        }));
        setTraceState('tracing');
        setError(null);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          switch (data.type) {
            case 'trace_started':
              setTraceInfo(data);
              break;
            case 'hop':
              setHops(prev => [...prev, data.data]);
              break;
            case 'trace_complete':
              traceCompletedRef.current = true;
              setTraceSummary(data);
              setAllHops(data.all_hops || data.hops || []);
              setVisibleHops(data.visible_hops || data.hops || []);
              setTraceState('complete');
              break;
            case 'error':
              setError(data.message);
              setTraceState('error');
              break;
            default:
              break;
          }
        } catch (e) {
          console.error('[Trace WS] Parse error:', e);
        }
      };

      ws.onerror = (err) => {
        console.error('[Trace WS] Connection error:', err);
        console.error('[Trace WS] WebSocket URL was:', wsUrl);
        console.error('[Trace WS] Backend URL:', process.env.REACT_APP_BACKEND_URL);
      };

      ws.onclose = (evt) => {
        console.log('[Trace WS] Connection closed:', evt.code, evt.reason);
        wsRef.current = null;
        if (traceCompletedRef.current || intentionalCloseRef.current) return;

        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current += 1;
          const delay = RETRY_DELAY_MS * retryCountRef.current;
          console.log(`[Trace WS] Retry attempt ${retryCountRef.current}/${MAX_RETRIES} in ${delay}ms`);
          setTraceState('connecting');
          retryTimerRef.current = setTimeout(() => {
            const params = pendingParamsRef.current;
            if (params) connectWebSocket(params.target, params.sourceIp, true);
          }, delay);
        } else {
          console.error('[Trace WS] Max retries reached. Connection failed.');
          setError('Connection failed. Please check your internet connection and try again.');
          setTraceState('error');
        }
      };
    } catch (e) {
      setError(e.message);
      setTraceState('error');
    }
  }, []);

  const startTrace = useCallback((target, sourceIp) => {
    cleanup();
    
    // Immediately clear all state for fresh start
    setHops([]);
    setAllHops([]);
    setVisibleHops([]);
    setTraceInfo(null);
    setTraceSummary(null);
    setError(null);
    setTraceState('idle');
    
    // Start immediately for faster UX
    connectWebSocket(target, sourceIp, false);
  }, [cleanup, connectWebSocket]);

  const retry = useCallback(() => {
    const params = pendingParamsRef.current;
    if (params) {
      cleanup();
      connectWebSocket(params.target, params.sourceIp, false);
    }
  }, [cleanup, connectWebSocket]);

  useEffect(() => {
    return () => {
      intentionalCloseRef.current = true;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch(e) {} }
    };
  }, []);

  return { traceState, hops, allHops, visibleHops, traceInfo, traceSummary, error, startTrace, retry, cleanup };
}
